import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { FaArrowLeft, FaExclamationTriangle, FaFileAlt, FaHistory, FaListUl, FaMoneyCheckAlt, FaRegFileAlt, FaThumbsUp } from 'react-icons/fa';
import ErpTemplate from '../../../components/ErpTemplate';
import PagamentoLegadoParcela from '../../../components/Financeiro/PagamentoLegadoParcela';
import DocumentosParcela from '../../../components/Financeiro/DocumentosParcela';
import { podeGerenciarDocAdiantamento, AVISO_SEM_PERMISSAO_DOC } from '../../../components/Financeiro/adiantamentoDocumentoPolicy';
import api from '../../../services/api';
import { moeda } from '../../../util/util';
import { formatarValorMoeda } from '../../../components/moedaOrigemPolicy';
import { agruparItensPorGrupo } from '../../Orcamento/itensFornecedorPolicy';
import { ehAdiantamento, situacaoAdiantamento, resumoItens, rotaDetalheAdiantamento, contarVoltas, periodoEvento, destinatarioRecibo, posDoEvento } from './adiantamentoDetalhePolicy';
import { useSetFullBreadcrumbTrail } from '../../../context/BreadcrumbContext';
import moment from 'moment';
import '../../../css/financeiro-parcela-pagar.css';

// Tela de DETALHE de um adiantamento (D-0097, Rafael 24/10). Abre pelo clique na linha ou pelo
// ⋮ → Detalhes das listas de adiantamento (Contas Pendentes/Pagas, Aprovados pelo Financeiro,
// Contas a Receber e Saldo de adiantamento). Mostra os itens solicitados, um a um, para o
// Financeiro montar o recibo do cliente. Só leitura: crédito, recibo e baixa seguem nas listas.
export default function DetalheAdiantamento() {
    const { id } = useParams();
    const history = useHistory();
    const token = localStorage.getItem('token');

    const [parcela, setParcela] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    // adiantamentoDetalhes: { itens, totais_por_moeda, orcamento, solicitacao }. null = carregando/sem dado.
    const [detalhe, setDetalhe] = useState(null);
    const [carregandoItens, setCarregandoItens] = useState(true);
    const [historico, setHistorico] = useState([]);

    const carregar = useCallback(() => {
        setCarregando(true);
        api({ method: 'get', action: '/financeiroParcela/' + id, token: token })
            .then((response) => {
                const dado = response.data?.data ?? response.data;
                if (!dado || !dado.id) { setErro('Adiantamento não encontrado.'); setParcela(null); }
                else if (!ehAdiantamento(dado)) { setErro('Esta conta não é um adiantamento.'); setParcela(null); }
                else { setParcela(dado); setErro(null); }
            })
            .catch((error) => {
                setErro(error.response?.data?.error ?? error.response?.data?.message ?? 'Erro ao carregar o adiantamento.');
                setParcela(null);
            })
            .finally(() => setCarregando(false));

        setCarregandoItens(true);
        api({ method: 'get', action: '/financeiroParcela/' + id + '/adiantamentoDetalhes', token: token })
            .then((response) => setDetalhe(response.data || null))
            .catch(() => setDetalhe(null))
            .finally(() => setCarregandoItens(false));

        // Histórico não bloqueia a tela; os documentos o componente DocumentosParcela carrega (D-0118).
        carregarHistorico();
    }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

    // Depois de substituir/excluir um documento, o histórico mostra quem fez (D-0118).
    function carregarHistorico() {
        api({ method: 'get', action: '/financeiroParcela/' + id + '/historico', token: token })
            .then((response) => setHistorico(response.data?.data ?? []))
            .catch(() => setHistorico([]));
    }

    useEffect(() => { carregar(); }, [carregar]);

    const setFullTrail = useSetFullBreadcrumbTrail();
    useEffect(() => {
        setFullTrail([
            { path: '/financeiro', label: 'Financeiro' },
            { path: '/financeiroSaldoAdiantamento', label: 'Adiantamentos' },
            { path: rotaDetalheAdiantamento(id), label: 'Detalhe do adiantamento' },
        ]);
    }, [setFullTrail, id]);

    const dataBr = (d) => (d ? moment(d).format('DD/MM/YYYY') : '—');
    const situacao = situacaoAdiantamento(parcela, dataBr);
    const itens = (detalhe && Array.isArray(detalhe.itens)) ? detalhe.itens : [];
    const fornecedoresComPrevisao = ((detalhe && detalhe.fornecedores) || []).filter((fornecedor) =>
        Array.isArray(fornecedor.parcelas_previsao) && fornecedor.parcelas_previsao.length > 0);
    const grupos = agruparItensPorGrupo(itens);
    const totaisItens = (detalhe && detalhe.totais_por_moeda) || {};
    const textoTotais = (totais) => Object.entries(totais).map(([m, v]) => formatarValorMoeda(v, m)).join(' + ') || '—';

    const evento = parcela && (parcela.titulo?.evento || null);
    const clienteNome = (evento && evento.cliente && evento.cliente.nome) || '—';
    const orcamento = detalhe && detalhe.orcamento;
    const solicitacao = detalhe && detalhe.solicitacao;

    const vencInfo = (function () {
        if (!parcela || !parcela.vencimento) return null;
        const d = moment(parcela.vencimento);
        if (!d.isValid()) return { texto: 'Data inválida', tom: 'is-danger', sub: '' };
        if (String(parcela.situacao) === '2' || parcela.status_pagamento === 'creditado') return { texto: d.format('DD/MM/YYYY'), tom: '', sub: 'Já creditado' };
        const dias = d.startOf('day').diff(moment().startOf('day'), 'days');
        if (dias < 0) return { texto: d.format('DD/MM/YYYY'), tom: 'is-danger', sub: 'Venceu há ' + Math.abs(dias) + (Math.abs(dias) === 1 ? ' dia' : ' dias') };
        if (dias === 0) return { texto: d.format('DD/MM/YYYY'), tom: 'is-warn', sub: 'Vence hoje' };
        return { texto: d.format('DD/MM/YYYY'), tom: '', sub: 'Em ' + dias + (dias === 1 ? ' dia' : ' dias') };
    })();

    // Linha rótulo/valor (visual do .fin-pp__tbl--kv, igual ao DetalheParcelaPagar).
    const Linha = ({ label, children }) => (
        <tr>
            <td>{label}</td>
            <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{children}</td>
        </tr>
    );

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Detalhe do adiantamento</h1>
                        <p className="fin-pp__subtitle">{parcela ? (parcela.descricao || 'Adiantamento') : 'Carregando…'}</p>
                    </div>
                    <div className="fin-pp__actions">
                        <button type="button" className="fin-pp__btn" onClick={() => history.goBack()}>
                            <FaArrowLeft size={15} /> Voltar
                        </button>
                    </div>
                </div>

                {carregando && (
                    <div className="fin-pp__card">
                        <div className="fin-pp__empty" style={{ padding: '48px 0' }}>Carregando…</div>
                    </div>
                )}

                {!carregando && erro && (
                    <div className="fin-pp__card">
                        <div className="fin-pp__empty" style={{ padding: '40px 0' }}>
                            <FaExclamationTriangle size={26} style={{ color: 'var(--fin-wn)' }} />
                            <div style={{ marginTop: 8 }}>{erro}</div>
                        </div>
                    </div>
                )}

                {!carregando && parcela && (<>
                    <section className="fin-pp__kpis" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Valor solicitado</span>
                                <span className="fin-pp__kpi-ic"><FaMoneyCheckAlt size={16} /></span>
                            </div>
                            <div className="fin-pp__kpi-value">R$ {moeda(parcela.valor)}</div>
                            <div className="fin-pp__kpi-sub">
                                {(parseInt(parcela.parcelas, 10) || 1) > 1 ? 'Parcela ' + ((parseInt(parcela.ordem, 10) || 0) + 1) + ' de ' + parcela.parcelas : 'Parcela única'}
                            </div>
                        </div>
                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Vencimento</span>
                                <span className="fin-pp__kpi-ic"><FaHistory size={16} /></span>
                            </div>
                            <div className={'fin-pp__kpi-value ' + (vencInfo ? vencInfo.tom : '')}>{vencInfo ? vencInfo.texto : '—'}</div>
                            <div className="fin-pp__kpi-sub">{vencInfo ? vencInfo.sub : 'Sem data'}</div>
                        </div>
                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Situação</span>
                                <span className={'fin-pp__kpi-ic' + (situacao.tom === 'is-ok' ? ' is-ok' : '')}><FaThumbsUp size={16} /></span>
                            </div>
                            <div className={'fin-pp__kpi-value ' + situacao.tom} style={{ fontSize: '1.15rem' }}>{situacao.texto}</div>
                            <div className="fin-pp__kpi-sub">{situacao.sub}</div>
                        </div>
                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Itens solicitados</span>
                                <span className="fin-pp__kpi-ic"><FaListUl size={16} /></span>
                            </div>
                            <div className="fin-pp__kpi-value" style={{ fontSize: '1.15rem' }}>{carregandoItens ? '…' : itens.length}</div>
                            <div className="fin-pp__kpi-sub">{carregandoItens ? 'Carregando…' : resumoItens(itens)}</div>
                        </div>
                    </section>

                    {/* ── Dados do adiantamento ── */}
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaFileAlt size={18} /></span>
                            <div>
                                <h2 className="fin-pp__cphead-title">Dados do adiantamento</h2>
                                <p className="fin-pp__cphead-sub">Cliente, evento, orçamento e aprovação</p>
                            </div>
                        </div>
                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                            <div className="fin-pp__tblwrap">
                                <table className="fin-pp__tbl fin-pp__tbl--kv">
                                    <tbody>
                                        <Linha label="Descrição"><strong>{parcela.descricao || '—'}</strong></Linha>
                                        <Linha label="Cliente">{clienteNome}</Linha>
                                        <Linha label="Evento">
                                            {(evento && evento.nome) || '—'}
                                            {evento && evento.codigo ? <span className="fin-pp__minitag is-info" style={{ marginLeft: 6 }}>#{evento.codigo}</span> : null}
                                        </Linha>
                                        {/* D-0118: o que o Financeiro precisa para emitir o recibo. */}
                                        <Linha label="Data do evento">{periodoEvento(parcela.dados_recibo)}</Linha>
                                        <Linha label="Recibo para">
                                            {destinatarioRecibo(parcela.dados_recibo) ? (function() {
                                                const d = destinatarioRecibo(parcela.dados_recibo);
                                                return (
                                                    <>
                                                        <strong>{d.nome}</strong>
                                                        {d.cargo ? <span className="fin-pp__cell-muted"> ({d.cargo})</span> : null}
                                                        {' · '}
                                                        {d.email ? <a href={'mailto:' + d.email}>{d.email}</a> : <span className="fin-pp__pill is-warn">sem e-mail cadastrado</span>}
                                                    </>
                                                );
                                            })() : (
                                                <span className="fin-pp__pill is-warn" title="Cadastre o responsável do cliente no evento">Responsável do cliente não cadastrado no evento</span>
                                            )}
                                        </Linha>
                                        <Linha label="P.O.">
                                            {(function() {
                                                const po = posDoEvento(parcela.dados_recibo);
                                                if (po.tipo === 'nao_aplica') return <span className="fin-pp__cell-muted">Não se aplica</span>;
                                                if (po.tipo === 'nenhuma') return <span className="fin-pp__pill is-warn">Nenhuma P.O. no evento</span>;
                                                return (
                                                    <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                                        {po.pos.map((item, i) => (
                                                            <span key={i} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                                                                <span className={'fin-pp__minitag ' + (item.aprovada ? 'is-ok' : 'is-info')}>
                                                                    {item.numero || 'sem número'}{item.aprovada ? ' · aprovada' : ''}
                                                                </span>
                                                                {item.arquivo_id ? (
                                                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm" title="Abrir o arquivo da P.O."
                                                                        onClick={() => window.open(process.env.REACT_APP_API_URL + '/arquivo/download/' + item.arquivo_id, '_blank')}>
                                                                        Abrir
                                                                    </button>
                                                                ) : <span className="fin-pp__cell-muted" style={{ fontSize: '0.75rem' }}>sem arquivo</span>}
                                                            </span>
                                                        ))}
                                                    </span>
                                                );
                                            })()}
                                        </Linha>
                                        <Linha label="Orçamento">
                                            {orcamento ? <>#{orcamento.codigo}{orcamento.titulo ? ' — ' + orcamento.titulo : ''}</> : <span className="fin-pp__cell-muted">—</span>}
                                        </Linha>
                                        <Linha label="Solicitado em">{parcela.created_at ? moment(parcela.created_at).format('DD/MM/YYYY HH:mm') : '—'}</Linha>
                                        {/* dataAprovacaoDiretor = recibo anexado ou crédito informado (não é aprovação de diretor). */}
                                        {parcela.dataAprovacaoDiretor && (
                                            <Linha label="Recibo anexado ou crédito informado em">
                                                {moment(parcela.dataAprovacaoDiretor).format('DD/MM/YYYY HH:mm')}
                                                {parcela.aprovado_diretor_por && parcela.aprovado_diretor_por.nome ? ' · ' + parcela.aprovado_diretor_por.nome : ''}
                                            </Linha>
                                        )}
                                        {parcela.liberado_financeiro_at && (
                                            <Linha label="Liberado para Contas Pendentes">{moment(parcela.liberado_financeiro_at).format('DD/MM/YYYY HH:mm')}</Linha>
                                        )}
                                        {parcela.dataReprovacaoDiretor && (
                                            <Linha label="Reprovado pelo diretor">
                                                <span style={{ color: 'var(--fin-dg)' }}>
                                                    {moment(parcela.dataReprovacaoDiretor).format('DD/MM/YYYY HH:mm')}
                                                    {parcela.motivoReprovacaoDiretor ? ' — ' + parcela.motivoReprovacaoDiretor : ''}
                                                </span>
                                            </Linha>
                                        )}
                                        {solicitacao && solicitacao.codigo_sap && (<Linha label="Código SAP">{solicitacao.codigo_sap}</Linha>)}
                                        {solicitacao && solicitacao.login_requisitante && (<Linha label="Requisitante (cliente)">{solicitacao.login_requisitante}</Linha>)}
                                        {parcela.observacao && (<Linha label="Observação">{parcela.observacao}</Linha>)}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </section>

                    {/* ── Itens solicitados: dado tabular = tabela, agrupada por grupo do orçamento ── */}
                    <section className="fin-pp__region" style={{ marginBottom: 18 }} data-itens-adiantamento>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaListUl size={18} /></span>
                            <div>
                                <h2 className="fin-pp__cphead-title">Itens solicitados</h2>
                                <p className="fin-pp__cphead-sub">O que está sendo pedido ao cliente, por grupo do orçamento — base do recibo</p>
                            </div>
                        </div>
                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                            {carregandoItens ? (
                                <div className="fin-pp__empty" style={{ padding: '32px 0' }}>Carregando…</div>
                            ) : itens.length === 0 ? (
                                <div className="fin-pp__empty" style={{ padding: '32px 0' }}>
                                    {detalhe ? 'Este adiantamento não tem itens do orçamento vinculados (valor avulso).' : 'Não foi possível carregar os itens.'}
                                </div>
                            ) : (<>
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                                        <thead><tr>
                                            <th>Item</th>
                                            <th>Fornecedor</th>
                                            <th>Etapa</th>
                                            <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>Valor</th>
                                        </tr></thead>
                                        {grupos.map((g) => (
                                            <tbody key={g.grupo_id} data-itens-grupo={g.grupo_id}>
                                                <tr className="fin-pp__tbl-sub">
                                                    <td colSpan={3} className="fin-pp__cell-strong">{g.grupo_nome}</td>
                                                    <td className="fin-pp__cell-num" style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>{textoTotais(g.totais)}</td>
                                                </tr>
                                                {g.itens.map((item) => (
                                                    <tr key={item.id}>
                                                        <td style={{ paddingLeft: 24 }}>
                                                            {item.item}
                                                            {item.pago_cliente && <span className="fin-pp__pill is-muted ms-2">Pago pelo cliente</span>}
                                                        </td>
                                                        <td className="fin-pp__cell-muted">{item.fornecedor_nome}</td>
                                                        <td className="fin-pp__cell-muted">{item.etapa}</td>
                                                        <td className="fin-pp__cell-num" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatarValorMoeda(item.valor, item.moeda)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        ))}
                                        <tbody>
                                            <tr className="fin-pp__tbl-sub">
                                                <td colSpan={3} className="fin-pp__cell-strong">Soma dos itens</td>
                                                <td className="fin-pp__cell-num" style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 700 }}>{textoTotais(totaisItens)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div className="fin-pp__cell-muted" style={{ padding: '10px 14px' }}>
                                    {/* D-0126: item com a foto da solicitação vem no valor cheio em R$ (câmbio + taxas do exterior, sem FEE). */}
                                    {itens.some((item) => item.valor_cheio)
                                        ? 'Valor de cada item = valor cheio em R$ na solicitação (câmbio e taxas do exterior, sem o FEE).'
                                        : 'Valor de cada item = última etapa preenchida (Fechamento, Negociado ou Pré-evento), na moeda do item.'}
                                    {' '}O adiantamento é o valor solicitado (R$ {moeda(parcela.valor)}); a soma dos itens é só referência.
                                </div>
                            </>)}
                        </div>
                    </section>

                    {fornecedoresComPrevisao.length > 0 && (
                        <section className="fin-pp__region" style={{ marginBottom: 18 }} data-pagamentos-legados-adiantamento>
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-ic"><FaMoneyCheckAlt size={18} /></span>
                                <div>
                                    <h2 className="fin-pp__cphead-title">Previsões dos itens deste adiantamento</h2>
                                    <p className="fin-pp__cphead-sub">Identificação de pagamento no processo antigo, por parcela prevista</p>
                                </div>
                            </div>
                            <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl">
                                        <thead><tr>
                                            <th>Fornecedor</th>
                                            <th>Previsão</th>
                                            <th>Pagamento no processo antigo</th>
                                            <th style={{ textAlign: 'right' }}>Valor</th>
                                        </tr></thead>
                                        <tbody>
                                            {fornecedoresComPrevisao.flatMap((fornecedor) =>
                                                fornecedor.parcelas_previsao.map((previsao, indice) => (
                                                    <tr key={previsao.id}>
                                                        <td>{fornecedor.fornecedor_nome}</td>
                                                        <td>Parcela {indice + 1}{previsao.data_pagamento ? ' · ' + dataBr(previsao.data_pagamento) : ''}</td>
                                                        <td><PagamentoLegadoParcela info={previsao.pagamento_legado} editavel={false} somenteMarcacao /></td>
                                                        <td className="fin-pp__cell-num" style={{ textAlign: 'right' }}>R$ {moeda(previsao.valor)}</td>
                                                    </tr>
                                                ))) }
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Histórico sempre aparece (como no modal antigo): as idas e voltas são o que o Financeiro cobra. */}
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaHistory size={18} /></span>
                            <div>
                                <h2 className="fin-pp__cphead-title">
                                    Histórico
                                    {contarVoltas(historico) > 0 && (
                                        <span className="fin-pp__minitag is-warn" style={{ marginLeft: 8 }} data-voltas-adiantamento={contarVoltas(historico)}>
                                            ↩ Voltou {contarVoltas(historico)}x
                                        </span>
                                    )}
                                </h2>
                                <p className="fin-pp__cphead-sub">Solicitação, aprovação, crédito e anexos, do mais antigo ao mais recente</p>
                            </div>
                        </div>
                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                            {historico.length === 0 ? (
                                <div className="fin-pp__empty" style={{ padding: '28px 0' }}>
                                    Sem registros ainda — as próximas idas e voltas (reenvio, reprovação, crédito) aparecem aqui.
                                </div>
                            ) : (
                            <div className="fin-pp__tblwrap">
                                <table className="fin-pp__tbl">
                                    <thead><tr>
                                        <th style={{ width: 150 }}>Quando</th>
                                        <th>O que aconteceu</th>
                                        <th style={{ width: 190 }}>Quem</th>
                                    </tr></thead>
                                    <tbody>
                                        {historico.map((h) => (
                                            <tr key={h.id}>
                                                <td style={{ whiteSpace: 'nowrap' }}>{h.data ? moment(h.data).format('DD/MM/YYYY HH:mm') : '—'}</td>
                                                <td>{h.descricao}</td>
                                                <td className="fin-pp__cell-muted">{h.usuario_nome || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            )}
                        </div>
                    </section>

                    <section className="fin-pp__region">
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaRegFileAlt size={18} /></span>
                            <div>
                                <h2 className="fin-pp__cphead-title">Documentos</h2>
                                <p className="fin-pp__cphead-sub">Recibo, comprovante e nota do adiantamento — o Financeiro substitui e exclui aqui</p>
                            </div>
                        </div>
                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                            {/* D-0118: abrir, substituir e excluir (Financeiro), com quem enviou. */}
                            <DocumentosParcela
                                parcelaId={parcela.id}
                                podeGerenciar={podeGerenciarDocAdiantamento(localStorage.getItem('perfil'))}
                                avisoSemPermissao={AVISO_SEM_PERMISSAO_DOC}
                                onAlterado={carregarHistorico}
                            />
                        </div>
                    </section>
                </>)}
            </div>
        </ErpTemplate>
    );
}
